/* eslint-disable import/no-unresolved */

'use client';

import { useEffect, useRef, useState } from 'react';
import {
  Autoplay, Pagination, Keyboard, A11y,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/a11y';
import './news.scss';
import PropTypes from 'prop-types';
import { CTAButton } from 'components/atoms/Button/Button';
import { goToCustomUrl } from 'controls/utils';
import {
  useIsMobile, focusSlideIntoView, pauseAutoplayOnFocus, resumeAutoplayOnBlur,
} from 'helpers/ui';
import { FallbackImage } from '@rcw/ui';
import DOMPurify from 'dompurify';

export default function News({ data }) {
  const swiperRef = useRef(null);
  const paginationRef = useRef(null);
  const [paginationReady, setPaginationReady] = useState(false);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const isMobile = useIsMobile();

  const newsData = (data?.elements || [])
    .filter((e) => e && e.enabled)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  useEffect(() => {
    if (paginationRef.current) setPaginationReady(true);
  }, []);

  return (
    <div
      className='news'
      onFocus={(e) => {
        focusSlideIntoView(swiperRef.current, e.target);
        pauseAutoplayOnFocus(swiperRef.current);
      }}
      onBlur={(e) => resumeAutoplayOnBlur(swiperRef.current, swiperRef.current?.el, e.relatedTarget)}
    >
      <div className='newsTitleContainer'>
        <div className='newsTitleContainer__title proxima-nova-bold' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.title) }} />
        <div className='newsTitleContainer__subtitle' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.body) }} />
      </div>
      {paginationReady && (
      <Swiper
        className={`newsCarousel ${newsData.length <= 3 && !isMobile ? 'centered' : 'spread'}`}
        modules={[Pagination, Autoplay, Keyboard, A11y]}
        autoplay={{ delay: 3000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        keyboard={{ enabled: true, onlyInViewport: true }}
        onBeforeInit={(swiper) => {
          swiperRef.current = swiper;
          swiper.params.pagination.el = paginationRef.current;
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        onAfterInit={(swiper) => {
          swiper.pagination.init();
          swiper.pagination.render();
          swiper.pagination.update();
          swiper.update();
        }}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        slidesPerView={isMobile ? 1 : 3}
      >
        {newsData.map((element, idx) => (
          <SwiperSlide key={element.order ?? element.title ?? idx} className='newsCarouselSlide'>
            <div className='newsCardContainer'>
              <FallbackImage
                src={element.thumbnailUrl}
                alt={DOMPurify.sanitize(element.title, { ALLOWED_TAGS: [] })}
                fetchPriority='high'
              />
              <div className='imgOverlay' />
              <div className='newsInfo'>
                <div className='cardTitle proxima-nova-bold' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.title) }} />
                <CTAButton ctaText={element.ctaText} className='newsCardBtn' onClick={() => goToCustomUrl(element.ctaUrl)} />
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      )}
      <div className='promoPagination' ref={paginationRef} />
      <div className='allNewsBtnContainer'>
        <CTAButton className='newsBtn' ctaText={data.ctaText} iconColor='#39A9F7' onClick={() => goToCustomUrl(data.ctaUrl)} />
      </div>
    </div>
  );
}

News.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string,
    ctaText: PropTypes.string,
    ctaUrl: PropTypes.string,
    elements: PropTypes.arrayOf(
      PropTypes.shape({
        enabled: PropTypes.bool,
        order: PropTypes.number,
        title: PropTypes.string,
        ctaText: PropTypes.string,
        ctaUrl: PropTypes.string,
        thumbnailUrl: PropTypes.string,
      }),
    ),
  }),
};
